import { useTranslator } from '@Localization';
import { type ChartData, ChartDataset, ChartOptions } from 'chart.js';
import { ReactElement } from 'react';
import { Chart } from 'react-chartjs-2';
import { IChartPoint, IChartPointString, IPrecisionRecall } from '../../../jobMetrics.model';
import { IValidJobType } from '../../../jobs.model';
import { getJobTranslationPrefix } from '../../../utils';

interface IProps {
  precisionRecall: IPrecisionRecall;
  isFinished?: boolean;
  jobType: IValidJobType;
}

export const PrecisionRecall = ({ precisionRecall, jobType, isFinished }: IProps): ReactElement => {
  const { t } = useTranslator();
  const translationPrefix = getJobTranslationPrefix(jobType);

  const roundAndFilterDecimal = (points: IChartPoint[]): IChartPointString[] => {
    const roundDecimal = points.map(({ x, y }) => ({ x: x.toFixed(3), y: y.toFixed(3) }));

    const seenX = new Set();

    return roundDecimal.filter((point) => {
      if (!seenX.has(point.x)) {
        seenX.add(point.x);
        return true;
      }
      return false;
    });
  };

  const getLineColors = (key: keyof IPrecisionRecall): string => {
    switch (key) {
      case 'validation':
        return '#e9a255';
      case 'train':
        return '#4859b5';
      case 'test':
      default:
        return '#b70000';
    }
  };

  const buildData = (precisionRecall: IPrecisionRecall): ChartData<'line', (number | IChartPointString | null)[], 'line'> => {
    const datasets = (Object.keys(precisionRecall) as [keyof IPrecisionRecall]).reduce(
      (acc: ChartDataset<'line', (number | IChartPointString | null)[]>[], key) => {
        const arr = precisionRecall[key];
        if (arr) {
          acc.push({
            label: t(`metrics.chart.${key}`),
            data: roundAndFilterDecimal(arr),
            fill: false,
            borderColor: getLineColors(key),
            clip: 5,
          });
        }
        return acc;
      },
      []
    );

    return {
      datasets,
    };
  };

  const options: ChartOptions<'line'> = {
    scales: {
      x: {
        type: 'linear',
        min: 0,
        max: 1,
        title: {
          font: { size: 18 },
          display: true,
          text: t('metrics.chart.xAxis'),
        },
        ticks: {
          stepSize: 0.1,
        },
      },
      y: {
        type: 'linear',
        min: 0,
        max: 1,
        title: {
          font: { size: 18 },
          display: true,
          text: t('metrics.chart.yAxis'),
        },
        ticks: {
          stepSize: 0.1,
        },
      },
    },
  };

  if (isFinished) {
    return <h5>{t(`${translationPrefix}.no_chart_for_current_process`)}</h5>;
  }

  return (
    <Chart
      type='line'
      className='precision-recall-curve'
      data={buildData(precisionRecall)}
      options={{ ...options, maintainAspectRatio: false }}
      height={200}
    />
  );
};
